import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useAppStore } from '@restorant/core';
import { useCatLabel, useT } from '../i18n';
import { useCategories, useMenuItems } from '../menu';
import type { IconName } from '../icons/Icon';
import { TabStrip } from '../components/TabStrip';
import { Screen } from '../components/Screen';
import { Chip } from '../components/Chip';
import { ManageMenuView } from './ManageMenuView';
import { ManageTablesView } from './ManageTablesView';
import { Icon } from '../icons/Icon';
import { T } from '../components/Text';
import { radius, useColors } from '../theme';
import { Pressable } from 'react-native';
import { EmptyNote } from '../components/EmptyNote';
import { ItemCard } from '../components/ItemCard';
import { ItemGrid } from '../components/ItemGrid';

type MenuMode = 'browse' | 'manage' | 'tables';

/** Browse | Manage | Tables switch at the top of the Menu tab. */
function ModeTabs({
  mode,
  onChange,
}: {
  mode: MenuMode;
  onChange: (m: MenuMode) => void;
}) {
  const t = useT();
  return (
    <TabStrip
      testIDPrefix="menu-mode"
      active={mode}
      onChange={(id) => onChange(id as MenuMode)}
      marginBottom={18}
      items={[
        { id: 'browse', label: t('browse'), icon: 'menu' },
        { id: 'manage', label: t('manage'), icon: 'edit' },
        { id: 'tables', label: t('tables_tab'), icon: 'table' },
      ]}
    />
  );
}

export function MenuScreen({ contentPadding }: { contentPadding: number }) {
  const t = useT();
  const [mode, setMode] = useState<MenuMode>('browse');
  const catLabel = useCatLabel();
  const activeCat = useAppStore((s) => s.activeCat);
  const search = useAppStore((s) => s.search);
  const setActiveCat = useAppStore((s) => s.setActiveCat);
  const categories = useCategories();
  const items = useMenuItems();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(
      (i) =>
        (activeCat === 'all' || i.cat === activeCat) &&
        (!q || i.name.toLowerCase().includes(q)),
    );
  }, [activeCat, search, items]);

  return (
    <Screen contentPadding={contentPadding}>
      <ModeTabs mode={mode} onChange={setMode} />

      {mode === 'manage' ? <ManageMenuView /> : null}
      {mode === 'tables' ? <ManageTablesView /> : null}

      {mode === 'browse' ? (
      <>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
        <Chip
          label={t('all')}
          icon="grid"
          active={activeCat === 'all'}
          onPress={() => setActiveCat('all')}
        />
        {categories.map((c) => (
          <Chip
            key={c.id}
            label={catLabel(c.id)}
            icon={c.icon as IconName}
            active={activeCat === c.id}
            onPress={() => setActiveCat(c.id)}
          />
        ))}
      </View>

      {filtered.length ? (
        <ItemGrid>
          {filtered.map((item) => (
            <ItemCard key={item.id} item={item} mode="manage" />
          ))}
        </ItemGrid>
      ) : (
        <EmptyNote>{t('no_match')}</EmptyNote>
      )}
      </>
      ) : null}
    </Screen>
  );
}
